import { HttpX } from '@99fun-agent/cores'
import { HttpXResponse } from '@99fun-agent/cores/src/http/HttpXResponse'
import { AxiosError } from 'axios'
import { ErrorCode } from '@99fun-agent/consts'

export type T_ForbidIpInfo = {
  Ip: string
  country: string
  isocode: string
}
let _siteRequest: HttpX
let _jwtProxy: () => string | undefined
let _jwtMissingProxy: () => void
let _tokenExpiredProxy: () => void

let _showLoadingProxy: (show: boolean) => void
let _showErrorCodeTipsProxy: (errorCode: ErrorCode) => void
let _showStrTipsProxy: (msg: string, isI18nKey: boolean) => void

let _needAuthProxy: () => void

let _forbidIpProxy: (ipInfo: T_ForbidIpInfo) => void

export type T_API_PAYLOAD = {
  /**
   * 是否显示loading
   */
  showLoading?: boolean
  /**
   * 成功后Tip的i18n key
   */
  successTipI18N?: string
  /**
   * 成功后Tip的具体文字
   */
  successTip?: string
  /**
   * 是否显示错误码Tips
   */
  showErrorCodeTip?: boolean
  /**
   * 可以忽略提示的错误码
   */
  skipErrorCode?: number[]
  /**
   * 失败后Tip的i18n key
   */
  errTipI18N?: string
  /**
   * 失败后tip的具体文字
   */
  errTip?: string

  /**
   * 是否需要登录
   */
  needAuth?: boolean
}

const init = (baseUrl: string, timeout: number = 10000) => {
  _siteRequest = new HttpX('HallApi', {
    baseURL: baseUrl,
    timeout: timeout,
    headers: {
      'Content-Type': 'application/json'
    }
  })

  _siteRequest.useRequestInterceptors({
    onFulFilled: (config) => {
      config.headers.Authorization = `Bearer ${_jwtProxy?.()}`
      return config
    }
  })

  _siteRequest.useResponseInterceptors({
    onFulfilled: (response) => {
      const r = new HttpXResponse()
      try {
        const { data, message, code } = response.data
        r.code = code
        r.data = data
        r.message = message
      } catch (error: any) {
        r.code = response.status
        r.message = error.message
      }
      response.data = r
      if (r.IsTokenExpired && !!_tokenExpiredProxy) {
        _tokenExpiredProxy()
      } else if (r.IsJwtMissing && !!_jwtMissingProxy) {
        _jwtMissingProxy()
      }
      return response
    },

    onRejected: (error: AxiosError) => {
      const r = new HttpXResponse()
      r.message = error.message

      if (error.response?.status !== undefined) {
        r.code = error.response.status
      }
      return Promise.reject(r)
    }
  })
}

const setJwtProxy = (proxy: () => string | undefined) => {
  _jwtProxy = proxy
}

const setJwpMissingProxy = (proxy: () => void) => {
  _jwtMissingProxy = proxy
}

const setTokenExpiredProxy = (proxy: () => void) => {
  _tokenExpiredProxy = proxy
}

/**
 * 设置开关loading代理方法
 * @param proxy
 */
const setShowLoadingProxy = (proxy: (show: boolean) => void) => {
  _showLoadingProxy = proxy
}

/**
 * 设置显示ErrorCode的Tips方法
 * @param proxy
 */
const setShowErrorCodeTipsProxy = (proxy: (errorCode: ErrorCode) => void) => {
  _showErrorCodeTipsProxy = proxy
}

/**
 * 设置显示字符串的Tips方法
 * @param proxy
 */
const setShowStrTipsProxy = (proxy: (msg: string, isI18nKey: boolean) => void) => {
  _showStrTipsProxy = proxy
}

/**
 * 设置需要登录回调
 * @param proxy
 */
const setNeedAuthProxy = (proxy: () => void) => {
  _needAuthProxy = proxy
}

/**
 * 设置ip禁止回调
 * @param proxy
 */
const setForbidIpProxy = (proxy: (info: T_ForbidIpInfo) => void) => {
  _forbidIpProxy = proxy
}

/**
 * 检查是否有登录信息
 * @returns
 */
const _checkHasAuth = () => {
  if (!_jwtProxy) return false
  const token = _jwtProxy()
  if (!token) return false
  return true
}

const _genNeedAuthResponse = <T>() => {
  const r = new HttpXResponse<T>()
  r.code = ErrorCode.ErrNeedAuth
  return r
}

const _switchLoading = (show: boolean, payload?: T_API_PAYLOAD) => {
  if (!_showLoadingProxy) return
  if (!payload?.showLoading) return
  _showLoadingProxy(show)
}

const _showTips = (resp: HttpXResponse<any>, payload?: T_API_PAYLOAD) => {
  if (resp.isSuccess) {
    //成功
    if (payload?.successTipI18N && _showStrTipsProxy) {
      _showStrTipsProxy(payload.successTipI18N, true)
    } else if (payload?.successTip && _showStrTipsProxy) {
      _showStrTipsProxy(payload.successTip, true)
    } else {
      //发送通用提示？？？
    }
    return
  }

  //失败
  if (payload?.showErrorCodeTip && _showErrorCodeTipsProxy) {
    const isSkipErrCode = (payload?.skipErrorCode ?? []).includes(resp.code)
    if (!isSkipErrCode) {
      _showErrorCodeTipsProxy(resp.code)
    }
  } else if (payload?.errTipI18N && _showStrTipsProxy) {
    _showStrTipsProxy(payload?.errTipI18N, true)
  } else if (payload?.errTip && _showStrTipsProxy) {
    _showStrTipsProxy(payload?.errTip, false)
  } else {
    //通用提示？？？？
  }
}

const _checkIpForbid = (resp: HttpXResponse<any>) => {
  if (resp.code !== ErrorCode.ErrUserIpFailed) return
  if (_forbidIpProxy) {
    _forbidIpProxy(JSON.parse(resp.message ?? '{}'))
  }
}

/**
 * POST 请求
 * @param path 请求路径
 * @param data 请求携带Body数据
 * @returns
 */
const post = async <T>(
  path: string,
  data?: any,
  payload?: T_API_PAYLOAD
): Promise<HttpXResponse<T>> => {
  if (payload?.needAuth && !_checkHasAuth()) {
    if (!!_needAuthProxy) _needAuthProxy()
    return _genNeedAuthResponse()
  }
  _switchLoading(true, payload)
  const r = await _siteRequest?.post<T>(path, data)
  _switchLoading(false, payload)
  _showTips(r, payload)
  _checkIpForbid(r)
  return r
}

/**
 * GET 请求
 * @param path 请求路径
 * @param params 请求携带QueryString参数
 * @returns
 */
const get = async <T>(
  path: string,
  params?: Record<string, any>,
  payload?: T_API_PAYLOAD
): Promise<HttpXResponse<T>> => {
  if (payload?.needAuth && !_checkHasAuth()) {
    if (!!_needAuthProxy) _needAuthProxy()
    return _genNeedAuthResponse()
  }
  _switchLoading(true, payload)
  const r = await _siteRequest?.get<T>(path, params)
  _switchLoading(false, payload)
  _showTips(r, payload)
  _checkIpForbid(r)
  return r
}

/**
 * 需要登录的POST
 * @param path
 * @param data
 * @param payload
 * @returns
 */
const authPost = async <T>(
  path: string,
  data?: any,
  payload?: T_API_PAYLOAD
): Promise<HttpXResponse<T>> => {
  payload = payload ?? {}
  payload.needAuth = true
  return post<T>(path, data, payload)
}

/**
 * 需要登录的GET
 * @param path
 * @param params
 * @param payload
 * @returns
 */
const authGet = async <T>(
  path: string,
  params?: Record<string, any>,
  payload?: T_API_PAYLOAD
): Promise<HttpXResponse<T>> => {
  payload = payload ?? {}
  payload.needAuth = true
  return get<T>(path, params, payload)
}

/**
 * 大厅API相关
 * @returns
 */
export const useHallApi = () => ({
  init,
  setJwtProxy,
  setTokenExpiredProxy,
  setJwpMissingProxy,
  setShowErrorCodeTipsProxy,
  setShowLoadingProxy,
  setShowStrTipsProxy,
  setNeedAuthProxy,
  setForbidIpProxy,
  post,
  get,
  authGet,
  authPost
})
